/**
 * Forum Ajax Block — Editor
 *
 * @package BBP_Core
 */
import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	TextControl,
	RangeControl,
	ColorPalette,
	Placeholder,
	Button,
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';
import './editor.scss';

/**
 * Skeleton loader component for the editor loading state.
 *
 * @return {JSX.Element} Skeleton loader markup.
 */
const SkeletonLoader = () => (
	<div className="bbpc-forum-ajax-skeleton">
		<div className="bbpc-forum-ajax-skeleton__filter" />
		{ [ 1, 2, 3 ].map( ( i ) => (
			<div key={ i } className="bbpc-forum-ajax-skeleton__card">
				<div className="bbpc-forum-ajax-skeleton__left">
					<div className="bbpc-forum-ajax-skeleton__title" />
					<div className="bbpc-forum-ajax-skeleton__meta" />
				</div>
				<div className="bbpc-forum-ajax-skeleton__right">
					<div className="bbpc-forum-ajax-skeleton__stat" />
					<div className="bbpc-forum-ajax-skeleton__stat" />
					<div className="bbpc-forum-ajax-skeleton__stat" />
				</div>
			</div>
		) ) }
	</div>
);

/**
 * Edit component for the Forum Ajax block.
 *
 * @param {Object}   props               Block props.
 * @param {Object}   props.attributes    Block attributes.
 * @param {Function} props.setAttributes Attribute setter.
 * @return {JSX.Element} Block editor output.
 */
export default function Edit( { attributes, setAttributes } ) {
	const blockProps = useBlockProps();
	const {
		ppp2,
		order,
		orderby,
		filter_btns,
		show_filter_all,
		show_filter_popular,
		show_filter_featured,
		show_filter_recent,
		show_filter_unloved,
		show_filter_loved,
		label_all,
		label_popular,
		label_featured,
		label_recent,
		label_unloved,
		label_loved,
		show_views,
		show_likes,
		show_replies,
		show_author,
		show_time,
		show_parent_forum,
		enable_card_hover,
		no_posts_message,
		forum_title_color,
		forum_title_hover_color,
		forum_meta_color,
		parent_forum_color,
		parent_forum_color_hover,
		card_bg_color,
		filter_bar_bg_color,
		active_filter_color,
		reach_color,
		title_font_size,
		meta_font_size,
		card_border_radius,
		card_gap,
		hide_reach_mobile,
		forum_filter_ids,
		exclude_forum_ids,
		show_excerpt,
		excerpt_length,
		show_avatar,
		show_status_badge,
		load_more,
		load_more_text,
	} = attributes;

	const isProActive =
		window.bbpc_upsell_config?.is_pro_active === '1' ||
		window.bbpc_upsell_config?.is_pro_active === true;
	const upsellImageUrl = window.bbpc_upsell_config?.upsell_image_url;
	const upgradeUrl = window.bbpc_upsell_config?.upgrade_url;

	// Upsell gate for free users.
	if ( ! isProActive ) {
		return (
			<div { ...blockProps }>
				<div
					className="bbpc-upsell-wrapper"
					style={ {
						position: 'relative',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '300px',
						background: '#f5f5f5',
						border: '1px dashed #ccc',
					} }
				>
					{ upsellImageUrl ? (
						<img
							src={ upsellImageUrl }
							alt="Pro Feature"
							style={ {
								maxWidth: '100%',
								height: 'auto',
								display: 'block',
							} }
						/>
					) : (
						<p>
							{ __( 'Image not found', 'bbp-core' ) }
						</p>
					) }

					<div
						style={ {
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 10,
						} }
					>
						<Button
							variant="primary"
							href={ upgradeUrl }
							target="_blank"
							className="bbpc-upgrade-btn"
						>
							{ __( 'Upgrade to Pro', 'bbp-core' ) }
						</Button>
					</div>
				</div>
			</div>
		);
	}

	const orderByOptions = [
		{ label: __( 'Date', 'bbp-core' ), value: 'date' },
		{ label: __( 'Title', 'bbp-core' ), value: 'title' },
		{ label: __( 'Modified', 'bbp-core' ), value: 'modified' },
		{
			label: __( 'Comment Count', 'bbp-core' ),
			value: 'comment_count',
		},
		{ label: __( 'ID', 'bbp-core' ), value: 'ID' },
	];

	const defaultColors = [
		{ name: __( 'Blue', 'bbp-core' ), color: '#3b82f6' },
		{ name: __( 'Green', 'bbp-core' ), color: '#10b981' },
		{ name: __( 'Red', 'bbp-core' ), color: '#ef4444' },
		{ name: __( 'Purple', 'bbp-core' ), color: '#8b5cf6' },
		{ name: __( 'Orange', 'bbp-core' ), color: '#f59e0b' },
		{ name: __( 'Gray', 'bbp-core' ), color: '#6b7280' },
		{ name: __( 'White', 'bbp-core' ), color: '#ffffff' },
		{ name: __( 'Black', 'bbp-core' ), color: '#000000' },
	];

	return (
		<div { ...blockProps }>
			{ /* ======== SETTINGS TAB ======== */ }
			<InspectorControls>
				{ /* -------- Filter Options -------- */ }
				<PanelBody title={ __( 'Filter Options', 'bbp-core' ) }>
					<TextControl
						label={ __( 'Show Topics', 'bbp-core' ) }
						type="number"
						value={ ppp2 }
						onChange={ ( value ) =>
							setAttributes( {
								ppp2: parseInt( value ),
							} )
						}
						help={ __(
							'Number of topics to show initially.',
							'bbp-core'
						) }
					/>
					<SelectControl
						label={ __( 'Order', 'bbp-core' ) }
						value={ order }
						options={ [
							{ label: 'ASC', value: 'ASC' },
							{ label: 'DESC', value: 'DESC' },
						] }
						onChange={ ( value ) =>
							setAttributes( { order: value } )
						}
					/>
					<SelectControl
						label={ __( 'Order By', 'bbp-core' ) }
						value={ orderby }
						options={ orderByOptions }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
					/>
					<TextControl
						label={ __( 'Forum IDs (Include)', 'bbp-core' ) }
						value={ forum_filter_ids }
						onChange={ ( value ) =>
							setAttributes( {
								forum_filter_ids: value,
							} )
						}
						help={ __(
							'Comma-separated forum IDs to include topics from.',
							'bbp-core'
						) }
					/>
					<TextControl
						label={ __( 'Forum IDs (Exclude)', 'bbp-core' ) }
						value={ exclude_forum_ids }
						onChange={ ( value ) =>
							setAttributes( {
								exclude_forum_ids: value,
							} )
						}
						help={ __(
							'Comma-separated forum IDs to exclude topics from.',
							'bbp-core'
						) }
					/>
				</PanelBody>

				{ /* -------- Tab Filter -------- */ }
				<PanelBody
					title={ __( 'Tab Filters', 'bbp-core' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show Filter Bar', 'bbp-core' ) }
						checked={ filter_btns }
						onChange={ ( value ) =>
							setAttributes( { filter_btns: value } )
						}
					/>
					{ filter_btns && (
						<>
							<p
								className="components-base-control__help"
								style={ { marginBottom: '8px' } }
							>
								{ __(
									'Toggle individual filters and customize labels:',
									'bbp-core'
								) }
							</p>

							<ToggleControl
								label={ __( 'All', 'bbp-core' ) }
								checked={ show_filter_all }
								onChange={ ( value ) =>
									setAttributes( {
										show_filter_all: value,
									} )
								}
							/>
							{ show_filter_all && (
								<TextControl
									label={ __(
										'All Label',
										'bbp-core'
									) }
									value={ label_all }
									onChange={ ( value ) =>
										setAttributes( {
											label_all: value,
										} )
									}
								/>
							) }

							<ToggleControl
								label={ __( 'Popular', 'bbp-core' ) }
								checked={ show_filter_popular }
								onChange={ ( value ) =>
									setAttributes( {
										show_filter_popular: value,
									} )
								}
							/>
							{ show_filter_popular && (
								<TextControl
									label={ __(
										'Popular Label',
										'bbp-core'
									) }
									value={ label_popular }
									onChange={ ( value ) =>
										setAttributes( {
											label_popular: value,
										} )
									}
								/>
							) }

							<ToggleControl
								label={ __( 'Featured', 'bbp-core' ) }
								checked={ show_filter_featured }
								onChange={ ( value ) =>
									setAttributes( {
										show_filter_featured: value,
									} )
								}
							/>
							{ show_filter_featured && (
								<TextControl
									label={ __(
										'Featured Label',
										'bbp-core'
									) }
									value={ label_featured }
									onChange={ ( value ) =>
										setAttributes( {
											label_featured: value,
										} )
									}
								/>
							) }

							<ToggleControl
								label={ __( 'Recent', 'bbp-core' ) }
								checked={ show_filter_recent }
								onChange={ ( value ) =>
									setAttributes( {
										show_filter_recent: value,
									} )
								}
							/>
							{ show_filter_recent && (
								<TextControl
									label={ __(
										'Recent Label',
										'bbp-core'
									) }
									value={ label_recent }
									onChange={ ( value ) =>
										setAttributes( {
											label_recent: value,
										} )
									}
								/>
							) }

							<ToggleControl
								label={ __( 'Unloved', 'bbp-core' ) }
								checked={ show_filter_unloved }
								onChange={ ( value ) =>
									setAttributes( {
										show_filter_unloved: value,
									} )
								}
							/>
							{ show_filter_unloved && (
								<TextControl
									label={ __(
										'Unloved Label',
										'bbp-core'
									) }
									value={ label_unloved }
									onChange={ ( value ) =>
										setAttributes( {
											label_unloved: value,
										} )
									}
								/>
							) }

							<ToggleControl
								label={ __( 'Loved', 'bbp-core' ) }
								checked={ show_filter_loved }
								onChange={ ( value ) =>
									setAttributes( {
										show_filter_loved: value,
									} )
								}
							/>
							{ show_filter_loved && (
								<TextControl
									label={ __(
										'Loved Label',
										'bbp-core'
									) }
									value={ label_loved }
									onChange={ ( value ) =>
										setAttributes( {
											label_loved: value,
										} )
									}
								/>
							) }
						</>
					) }
				</PanelBody>

				{ /* -------- Visibility -------- */ }
				<PanelBody
					title={ __( 'Visibility', 'bbp-core' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show Author', 'bbp-core' ) }
						checked={ show_author }
						onChange={ ( value ) =>
							setAttributes( { show_author: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Time', 'bbp-core' ) }
						checked={ show_time }
						onChange={ ( value ) =>
							setAttributes( { show_time: value } )
						}
					/>
					<ToggleControl
						label={ __(
							'Show Parent Forum',
							'bbp-core'
						) }
						checked={ show_parent_forum }
						onChange={ ( value ) =>
							setAttributes( {
								show_parent_forum: value,
							} )
						}
					/>
					<ToggleControl
						label={ __( 'Show Views', 'bbp-core' ) }
						checked={ show_views }
						onChange={ ( value ) =>
							setAttributes( { show_views: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Likes', 'bbp-core' ) }
						checked={ show_likes }
						onChange={ ( value ) =>
							setAttributes( { show_likes: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Replies', 'bbp-core' ) }
						checked={ show_replies }
						onChange={ ( value ) =>
							setAttributes( { show_replies: value } )
						}
					/>
					<ToggleControl
						label={ __(
							'Enable Card Hover Animation',
							'bbp-core'
						) }
						checked={ enable_card_hover }
						onChange={ ( value ) =>
							setAttributes( {
								enable_card_hover: value,
							} )
						}
					/>
					<ToggleControl
						label={ __( 'Show Topic Excerpt', 'bbp-core' ) }
						checked={ show_excerpt }
						onChange={ ( value ) =>
							setAttributes( {
								show_excerpt: value,
							} )
						}
					/>
					{ show_excerpt && (
						<RangeControl
							label={ __(
								'Excerpt Words',
								'bbp-core'
							) }
							value={ excerpt_length }
							onChange={ ( value ) =>
								setAttributes( {
									excerpt_length: value,
								} )
							}
							min={ 5 }
							max={ 60 }
						/>
					) }
					<ToggleControl
						label={ __(
							'Show Author Avatar',
							'bbp-core'
						) }
						checked={ show_avatar }
						onChange={ ( value ) =>
							setAttributes( {
								show_avatar: value,
							} )
						}
					/>
					<ToggleControl
						label={ __(
							'Show Topic Status Badge',
							'bbp-core'
						) }
						checked={ show_status_badge }
						onChange={ ( value ) =>
							setAttributes( {
								show_status_badge: value,
							} )
						}
						help={ __(
							'Displays Open, Closed, or Resolved badge.',
							'bbp-core'
						) }
					/>
				</PanelBody>

				{ /* -------- General -------- */ }
				<PanelBody
					title={ __( 'General', 'bbp-core' ) }
					initialOpen={ false }
				>
					<TextControl
						label={ __(
							'"No Topics Found" Message',
							'bbp-core'
						) }
						value={ no_posts_message }
						onChange={ ( value ) =>
							setAttributes( {
								no_posts_message: value,
							} )
						}
					/>
				</PanelBody>

				{ /* -------- Responsive -------- */ }
				<PanelBody
					title={ __( 'Responsive', 'bbp-core' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __(
							'Hide Views/Likes/Replies on Mobile',
							'bbp-core'
						) }
						checked={ hide_reach_mobile }
						onChange={ ( value ) =>
							setAttributes( {
								hide_reach_mobile: value,
							} )
						}
					/>
				</PanelBody>

				{ /* -------- Load More -------- */ }
				<PanelBody
					title={ __( 'Load More', 'bbp-core' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __(
							'Enable Load More Button',
							'bbp-core'
						) }
						checked={ load_more }
						onChange={ ( value ) =>
							setAttributes( {
								load_more: value,
							} )
						}
					/>
					{ load_more && (
						<TextControl
							label={ __(
								'Button Text',
								'bbp-core'
							) }
							value={ load_more_text }
							onChange={ ( value ) =>
								setAttributes( {
									load_more_text: value,
								} )
							}
						/>
					) }
				</PanelBody>
			</InspectorControls>

			{ /* ======== STYLES TAB ======== */ }
			<InspectorControls group="styles">
				{ /* -------- Colors -------- */ }
				<PanelBody
					title={ __( 'Colors', 'bbp-core' ) }
					initialOpen={ false }
				>
					<p className="bbpc-color-label">
						{ __( 'Topic Title Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ forum_title_color }
						onChange={ ( value ) =>
							setAttributes( {
								forum_title_color: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __(
							'Topic Title Hover Color',
							'bbp-core'
						) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ forum_title_hover_color }
						onChange={ ( value ) =>
							setAttributes( {
								forum_title_hover_color: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Meta Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ forum_meta_color }
						onChange={ ( value ) =>
							setAttributes( {
								forum_meta_color: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Parent Forum Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ parent_forum_color }
						onChange={ ( value ) =>
							setAttributes( {
								parent_forum_color: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __(
							'Parent Forum Hover Color',
							'bbp-core'
						) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ parent_forum_color_hover }
						onChange={ ( value ) =>
							setAttributes( {
								parent_forum_color_hover: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Card Background', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ card_bg_color }
						onChange={ ( value ) =>
							setAttributes( {
								card_bg_color: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __(
							'Filter Bar Background',
							'bbp-core'
						) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ filter_bar_bg_color }
						onChange={ ( value ) =>
							setAttributes( {
								filter_bar_bg_color: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Active Filter Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ active_filter_color }
						onChange={ ( value ) =>
							setAttributes( {
								active_filter_color: value,
							} )
						}
					/>

					<p className="bbpc-color-label">
						{ __(
							'Views/Likes/Replies Color',
							'bbp-core'
						) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ reach_color }
						onChange={ ( value ) =>
							setAttributes( { reach_color: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Typography -------- */ }
				<PanelBody
					title={ __( 'Typography', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __(
							'Title Font Size (px)',
							'bbp-core'
						) }
						value={ title_font_size }
						onChange={ ( value ) =>
							setAttributes( {
								title_font_size: value,
							} )
						}
						min={ 0 }
						max={ 48 }
					/>
					<RangeControl
						label={ __(
							'Meta Font Size (px)',
							'bbp-core'
						) }
						value={ meta_font_size }
						onChange={ ( value ) =>
							setAttributes( {
								meta_font_size: value,
							} )
						}
						min={ 0 }
						max={ 32 }
					/>
				</PanelBody>

				{ /* -------- Spacing -------- */ }
				<PanelBody
					title={ __( 'Spacing', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __(
							'Card Border Radius (px)',
							'bbp-core'
						) }
						value={ card_border_radius }
						onChange={ ( value ) =>
							setAttributes( {
								card_border_radius: value,
							} )
						}
						min={ 0 }
						max={ 30 }
					/>
					<RangeControl
						label={ __( 'Card Gap (px)', 'bbp-core' ) }
						value={ card_gap }
						onChange={ ( value ) =>
							setAttributes( { card_gap: value } )
						}
						min={ 0 }
						max={ 40 }
					/>
				</PanelBody>
			</InspectorControls>

			{ /* ======== BLOCK PREVIEW ======== */ }
			<ServerSideRender
				block="bbp-core/forum-ajax"
				attributes={ attributes }
				LoadingResponsePlaceholder={ SkeletonLoader }
				ErrorResponsePlaceholder={ () => (
					<Placeholder
						icon="warning"
						label={ __(
							'BBPC Forums with Ajax Filters',
							'bbp-core'
						) }
					>
						{ __(
							'Error loading block preview.',
							'bbp-core'
						) }
					</Placeholder>
				) }
			/>
		</div>
	);
}